import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API_BASE, formatErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

function money(value) {
  return `₹ ${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] uppercase tracking-[0.16em] text-[#6B7280]">{label}</Label>
      {children}
    </div>
  );
}

export default function PublicSocietyBillPayment() {
  const { billId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ amount: "", method: "upi", utr: "", proofUrl: "", remarks: "" });

  const flat = useMemo(() => [bill?.wing, bill?.flatNumber].filter(Boolean).join(" "), [bill]);

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const res = await axios.get(`${API_BASE}/public/society/bills/${billId}`, { params: { token } });
        if (!alive) return;
        setBill(res.data);
        setForm((cur) => ({ ...cur, amount: String(res.data?.balanceDue || res.data?.totalDue || "") }));
      } catch (err) {
        toast.error(formatErr(err, "Bill link is invalid or expired"));
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => { alive = false; };
  }, [billId, token]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_BASE}/public/society/bills/${billId}/payments`, {
        ...form,
        amount: Number(form.amount || 0),
      }, { params: { token } });
      setSubmitted(true);
      toast.success("Payment details submitted for verification");
    } catch (err) {
      toast.error(formatErr(err, "Could not submit payment details"));
    }
  };

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center bg-[#F8FAFC] text-sm text-[#6B7280]">Loading bill...</div>;
  }

  if (!bill) {
    return <div className="flex min-h-screen items-center justify-center bg-[#F8FAFC] p-6 text-center text-sm text-[#6B7280]">This bill link is not available.</div>;
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] px-4 py-6">
      <div className="mx-auto max-w-3xl space-y-5">
        <div className="rounded-sm border border-[#E5E7EB] bg-white p-5">
          <div className="text-xs uppercase tracking-[0.18em] text-[#6B7280]">{bill.billTitle || "Society Maintenance Bill"}</div>
          <h1 className="mt-2 text-2xl font-extrabold text-[#111827]">{bill.billNumber}</h1>
          <div className="mt-2 text-sm text-[#6B7280]">{bill.billingMonth} | {flat || "Flat"} | {bill.ownerName || "Resident"}</div>
          <div className="mt-5 grid gap-3 sm:grid-cols-3">
            <div className="rounded-sm bg-[#F8FAFC] p-3"><div className="text-xs text-[#6B7280]">Total</div><div className="font-mono text-lg font-bold">{money(bill.totalDue)}</div></div>
            <div className="rounded-sm bg-[#F8FAFC] p-3"><div className="text-xs text-[#6B7280]">Paid</div><div className="font-mono text-lg font-bold">{money(bill.paidAmount)}</div></div>
            <div className="rounded-sm bg-[#111827] p-3 text-white"><div className="text-xs text-white/70">Balance</div><div className="font-mono text-lg font-bold">{money(bill.balanceDue)}</div></div>
          </div>
          <a
            className="mt-4 inline-flex min-h-10 items-center rounded-sm border border-[#D1D5DB] px-4 text-sm font-semibold text-[#111827] hover:bg-[#F9FAFB]"
            href={`${API_BASE}/public/society/bills/${billId}/pdf?token=${encodeURIComponent(token)}`}
            target="_blank"
            rel="noreferrer"
          >
            Download bill PDF
          </a>
        </div>

        <div className="grid gap-5 lg:grid-cols-[1fr_360px]">
          <div className="rounded-sm border border-[#E5E7EB] bg-white p-5">
            <h2 className="text-lg font-extrabold text-[#111827]">Submit payment details</h2>
            {submitted ? (
              <div className="mt-5 rounded-sm border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
                Your payment details are submitted. Treasurer will verify and update the bill status.
              </div>
            ) : (
              <form onSubmit={submit} className="mt-4 grid gap-4">
                <Field label="Paid amount"><Input type="number" min="1" step="0.01" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} required /></Field>
                <Field label="Payment method">
                  <Select value={form.method} onValueChange={(v) => setForm((f) => ({ ...f, method: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                      <SelectItem value="cheque">Cheque</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="UTR / reference number"><Input value={form.utr} onChange={(e) => setForm((f) => ({ ...f, utr: e.target.value }))} required={["upi", "bank_transfer"].includes(form.method)} /></Field>
                <Field label="Proof URL"><Input value={form.proofUrl} onChange={(e) => setForm((f) => ({ ...f, proofUrl: e.target.value }))} placeholder="Optional screenshot link" /></Field>
                <Field label="Remarks"><Textarea rows={3} value={form.remarks} onChange={(e) => setForm((f) => ({ ...f, remarks: e.target.value }))} /></Field>
                <Button type="submit" className="bg-[#111827] text-white hover:bg-[#374151]">Submit for verification</Button>
              </form>
            )}
          </div>

          <div className="rounded-sm border border-[#E5E7EB] bg-white p-5">
            <h2 className="text-lg font-extrabold text-[#111827]">Payment details</h2>
            {bill.bank?.upiId ? (
              <div className="mt-4 flex flex-col items-center rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] p-4">
                <img
                  src={`${API_BASE}/public/society/bills/${billId}/qr.png?token=${encodeURIComponent(token)}`}
                  alt="Scan to pay via UPI"
                  className="h-40 w-40"
                />
                <div className="mt-2 text-xs text-[#6B7280]">Scan with another phone's UPI app to pay {money(bill.balanceDue)}</div>
                <a
                  className="mt-3 inline-flex min-h-9 w-full items-center justify-center rounded-sm bg-[#111827] px-4 text-sm font-semibold text-white hover:bg-[#374151]"
                  href={`upi://pay?pa=${encodeURIComponent(bill.bank.upiId)}&pn=${encodeURIComponent(bill.bank.bankAccountName || "RP Signage Society")}&am=${Number(bill.balanceDue || 0).toFixed(2)}&cu=INR&tn=${encodeURIComponent(`${bill.billNumber || "Maintenance"} ${bill.billingMonth || ""}`.trim())}`}
                >
                  Pay now with UPI app on this phone
                </a>
              </div>
            ) : null}
            <div className="mt-4 space-y-3 text-sm">
              <div><div className="text-xs uppercase tracking-[0.16em] text-[#6B7280]">UPI</div><div className="font-mono">{bill.bank?.upiId || "-"}</div></div>
              <div><div className="text-xs uppercase tracking-[0.16em] text-[#6B7280]">Bank</div><div>{bill.bank?.bankName || "-"}</div></div>
              <div><div className="text-xs uppercase tracking-[0.16em] text-[#6B7280]">Account name</div><div>{bill.bank?.bankAccountName || "-"}</div></div>
              <div><div className="text-xs uppercase tracking-[0.16em] text-[#6B7280]">Account no.</div><div className="font-mono">{bill.bank?.bankAccountNumber || "-"}</div></div>
              <div><div className="text-xs uppercase tracking-[0.16em] text-[#6B7280]">IFSC</div><div className="font-mono">{bill.bank?.ifsc || "-"}</div></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
